import { HTTPError, FastRPCError, TimeoutError, AbortError } from "~/lib/net/errors";
import { serializeCall, parse, Hints } from "~/lib/net/fastrpc";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/browser";

interface Header {
	name: string;
	value: string;
}

export interface Options {
	timeout: number;
	hints: Hints;
	url: string;
	hashId: string;
	headers: Array<Header>;
	type: "frpc";
	withCredentials: boolean;
}

const OPTIONS: Options = {
	timeout: 0,
	hints: {},
	url: "/RPC2",
	hashId: "",
	headers: [],
	type: "frpc",
	withCredentials: true,
};

export const STATUS_OK = 200;
export const STATUS_ERROR = 400;
export const STATUS_REDIRECT = 300;

const NO_FUNC = function () {};

function findUndefinedProperties(obj, parentKey = "") {
	let undefinedProperties = [];

	if (typeof obj !== "object" || obj === null) {
		return undefinedProperties;
	}

	for (const key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			const fullKey = parentKey ? `${parentKey}.${key}` : key;

			if (obj[key] === undefined) {
				undefinedProperties.push(fullKey);
			} else if (typeof obj[key] === "object" && obj[key] !== null) {
				undefinedProperties = undefinedProperties.concat(findUndefinedProperties(obj[key], fullKey));
			}
		}
	}

	return undefinedProperties;
}

export default class Request {
	_resolve: (value: any) => void;

	_reject: (value: any) => void;

	_options: Options;

	_xhr: XMLHttpRequest;

	_promise: Promise<object>;

	_correlationId: string;

	constructor(method: string, args: any[] = [], options: Partial<Options> = {}) {
		this._resolve = NO_FUNC;
		this._reject = NO_FUNC;
		this._options = {
			...OPTIONS,
			...options,
		};
		this._correlationId = uuidv4();

		this._promise = new Promise((resolve, reject) => {
			this._resolve = resolve;
			this._reject = reject;
		});

		const xhr = new XMLHttpRequest();

		if (this._options.withCredentials) {
			xhr.withCredentials = true;
		}

		xhr.addEventListener("abort", this);
		xhr.addEventListener("error", this);
		xhr.addEventListener("timeout", this);
		xhr.addEventListener("load", this);
		xhr.open("POST", this._options.url, true);

		if ("timeout" in xhr && typeof xhr.timeout !== "function") {
			// v IE lze az po .open()
			xhr.timeout = this._options.timeout;
		}

		if (this._options.hashId) {
			xhr.setRequestHeader("X-Seznam-hashId", this._options.hashId);
		}

		xhr.setRequestHeader("X-Correlation-Id", this._correlationId);

		for (let index = 0; index < this._options.headers.length; index++) {
			const { name, value } = this._options.headers[index];

			xhr.setRequestHeader(name, value);
		}

		if (this._options.type === "frpc" && Uint8Array) {
			xhr.responseType = "arraybuffer";
			xhr.setRequestHeader("Accept", "application/x-frpc");
			xhr.setRequestHeader("Content-Type", "application/x-frpc");

			try {
				xhr.send(new Uint8Array(serializeCall(method, args, options.hints)));
			} catch (exc) {
				// eslint-disable-next-line no-console
				console.log(exc);
				// eslint-disable-next-line no-console
				console.log(`undefined values method ${method}: `, findUndefinedProperties(args));
				Sentry.withScope(scope => {
					scope.setExtras({
						method,
						args,
						options,
					});
					Sentry.captureException(exc);
				});
				throw exc;
			}
		} else {
			throw new Error("Unknown RPC type, frpc only supported or no Uint8Array!");
		}

		this._xhr = xhr;
	}

	getPromise() {
		return this._promise;
	}

	getXHR() {
		return this._xhr;
	}

	get correlationId() {
		return this._correlationId;
	}

	abort() {
		if (this._xhr) {
			this._xhr.abort();
			this._reject(new AbortError(this._correlationId));
		}
	}

	handleEvent(event: Event) {
		let error = null;

		switch (event.type) {
			case "abort":
				this._reject(new AbortError(this._correlationId));
				break;

			case "error":
				error = new HTTPError(this._xhr.status, this._xhr.statusText, this._correlationId);
				this._reject(error);
				break;

			case "timeout":
				error = new TimeoutError(this._correlationId);
				this._reject(error);
				break;

			case "load":
				this._response(this._xhr);
				break;

			default:
				// nic
		}
	}

	_response(xhr: XMLHttpRequest) {
		const status = xhr.status;

		if (status < STATUS_OK || status >= STATUS_REDIRECT) {
			this._reject(new HTTPError(status, xhr.statusText || ""));

			return;
		}

		let data = null;

		if (this._options.type === "frpc") {
			if (Uint8Array && xhr.response instanceof ArrayBuffer) {
				data = parse(new Uint8Array(xhr.response));
			}
		}

		if (!data) {
			this._reject(new FastRPCError(STATUS_ERROR, "Cannot parse response, or no respone"));
		}

		// musime vracet data, ne chybu!
		if ("status" in data && (data.status < STATUS_OK || data.status >= STATUS_REDIRECT)) {
			this._reject(data);

			return;
		}

		this._resolve(data);
	}
}
